'use client';

import React, { useState } from 'react';
import { Search, Calendar, Menu, ChevronDown, User, LogOut, Settings } from 'lucide-react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import NotificationWidget from './NotificationWidget';

interface HeaderProps {
    onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
    const pathname = usePathname();
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const getRoleTitle = () => {
        if (pathname.startsWith('/sekolah') || pathname.match(/^\/yayasan\/list-sekolah\/(paud|sd|smp|smk)/)) return 'Bendahara Unit';
        if (pathname.startsWith('/intray')) return 'System Administrator';
        return 'Bendahara Yayasan';
    };

    const getRoleSubtitle = () => {
        if (pathname.startsWith('/sekolah') || pathname.match(/^\/yayasan\/list-sekolah\/(paud|sd|smp|smk)/)) return 'Unit Admin';
        if (pathname.startsWith('/intray')) return 'Intray Admin';
        return 'Global Admin';
    };

    return (
        <header className="h-20 md:h-24 bg-background/40 backdrop-blur-xl px-4 md:px-10 py-5 border-b border-border-main flex items-center justify-between sticky top-0 z-40">
            <div className="flex items-center gap-4 flex-1 max-w-2xl">
                <button onClick={onMenuClick} className="lg:hidden p-1 -ml-2 text-secondary hover:text-primary transition-colors">
                    <Menu className="w-6 h-6" />
                </button>
                <div className="relative group flex-1 mr-8 hidden md:block">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted group-focus-within:text-primary transition-colors" />
                    <input
                        type="text"
                        placeholder="Cari data keuangan, unit, atau laporan..."
                        className="w-full bg-surface border border-border-main rounded-full py-3.5 pl-14 pr-6 text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none font-sans placeholder:text-muted placeholder:font-medium shadow-sm"
                    />
                </div>
            </div>

            <div className="flex items-center gap-6 font-sans">
                <div className="hidden lg:flex items-center gap-4 bg-surface/60 p-1.5 pr-5 rounded-full border border-border-main shadow-sm hover:shadow-md transition-all cursor-pointer group">
                    <div className="w-10 h-10 rounded-full bg-primary-soft/50 flex items-center justify-center group-hover:bg-primary-soft transition-colors">
                        <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <p className="text-[10px] text-secondary uppercase font-bold tracking-widest leading-none mb-1">Periode Aktif</p>
                        <p className="text-xs font-bold text-foreground">Smtr Ganjil 24/25</p>
                    </div>
                </div>

                <NotificationWidget />

                <div className="flex items-center gap-4 pl-6 border-l border-border-main/50 relative">
                    <button
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-4 focus:outline-none group/profile p-1.5 pr-4 rounded-3xl hover:bg-surface border border-transparent hover:border-border-main transition-all"
                    >
                        <div className="hidden lg:flex flex-col items-end">
                            <p className="text-sm font-bold text-foreground tracking-tight text-right group-hover/profile:text-primary transition-colors">{getRoleTitle()}</p>
                            <p className="text-[10px] text-primary font-bold uppercase tracking-widest">{getRoleSubtitle()}</p>
                        </div>
                        <div className="relative">
                            <div className="w-12 h-12 rounded-[1.25rem] bg-primary flex items-center justify-center text-white font-black text-xl shadow-lg shadow-primary/20 transition-transform group-hover/profile:scale-105 border-2 border-surface">
                                {getRoleTitle().charAt(0)}
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success border-4 border-surface rounded-full shadow-sm"></div>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-muted transition-transform group-hover/profile:text-primary ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Profile Dropdown */}
                    {isProfileOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-0"
                                onClick={() => setIsProfileOpen(false)}
                            ></div>
                            <div className="absolute top-full mt-4 right-0 w-72 bg-surface border border-border-main rounded-[2.5rem] shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-200 p-5 overflow-hidden font-sans">
                                <div className="px-3 py-4 mb-4">
                                    <p className="text-sm font-bold text-foreground leading-tight">{getRoleTitle()}</p>
                                    <p className="text-[11px] text-muted font-medium mt-1 truncate">admin.finance@yayasan.sch.id</p>
                                </div>
                                <div className="h-px bg-border-main/50 mb-4 mx-2"></div>
                                <div className="space-y-1">
                                    <Link
                                        href="/yayasan/profil"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-secondary hover:bg-primary-soft hover:text-primary rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <User className="w-5 h-5 opacity-50 group-hover/item:opacity-100 transition-opacity" /> Profil Saya
                                    </Link>
                                    <Link
                                        href="/yayasan/settings"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-secondary hover:bg-primary-soft hover:text-primary rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <Settings className="w-5 h-5 opacity-50 group-hover/item:opacity-100 transition-opacity" /> Pengaturan
                                    </Link>
                                    <div className="h-px bg-border-main/50 my-4 mx-2"></div>
                                    <Link
                                        href="/login"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-error hover:bg-error-soft rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <LogOut className="w-5 h-5 opacity-70 group-hover/item:opacity-100 transition-opacity" /> Keluar
                                    </Link>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>

    );
}


